import React, { useCallback, useRef, useEffect } from 'react';
import { useCanvasAnimation } from './useCanvasAnimation';

export interface ExplosionSimulationProps {
  size: number;
  color: string;
  particleCount: number;
  repeat: boolean;
}

interface ExplosionParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
}

export function ExplosionSimulation({
  size,
  color,
  particleCount,
  repeat,
}: ExplosionSimulationProps) {
  const particlesRef = useRef<ExplosionParticle[]>([]);

  const initParticles = useCallback(() => {
    const center = size / 2;
    const particles: ExplosionParticle[] = [];
    for (let i = 0; i < particleCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * 3 + 2;
      particles.push({
        x: center,
        y: center,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 1.0,
        maxLife: Math.random() * 0.5 + 0.5,
        size: Math.random() * 3 + 1,
      });
    }
    particlesRef.current = particles;
  }, [size, particleCount]);

  useEffect(() => {
    initParticles();
  }, [initParticles]);

  const draw = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      ctx.clearRect(0, 0, size, size);
      
      const particles = particlesRef.current;
      let allDead = true;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        if (p.life <= 0) continue;
        
        allDead = false;

        // Update
        p.x += p.vx;
        p.y += p.vy;
        p.life -= 0.01 / p.maxLife;

        // Friction
        p.vx *= 0.98;
        p.vy *= 0.98;

        // Draw
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.globalAlpha = Math.max(0, p.life);
        ctx.fill();
      }
      
      ctx.globalAlpha = 1.0;

      if (allDead && repeat) {
        initParticles();
      }
    },
    [size, color, repeat, initParticles]
  );

  const canvasRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ width: size, height: size }} className="relative flex items-center justify-center">
      <canvas ref={canvasRef} className="block w-full h-full" />
    </div>
  );
}
